import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Trash2, Edit, AlertCircle, Copy } from 'lucide-react';
import { Destinations, Pipelines } from '../../components/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LabsAppBar from "../../components/LabsAppBar";
import { ActionButton } from '../../components/LabsAppBar';
import TableComponent from '@components/shared/TableComponent';
import { ColumnDef } from '@tanstack/react-table';
import { Playbook } from '../../types/playbook';
import { fetchAllPlaybooks, deletePlaybook, duplicatePlaybook } from '../../services/playbookApiService'; 
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmDialog from '../../components/dialogs/ConfirmDialog';

const PlaybookListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotifications();

  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<Playbook | null>(null);

  const isPipelinePlaybooks = location.pathname === '/labs/playbooks';
  const playbookType = isPipelinePlaybooks ? 'pipeline' : 'integration';

  const pageTitle = isPipelinePlaybooks ? 'Pipeline Transforms' : 'Playbook Editor';
  const pageIcon = isPipelinePlaybooks ? 'Pipelines' : 'Destinations'; // Use the same icons as LabsNavbar

  const breadcrumbs = [{ name: pageTitle, iconName: pageIcon }];
  const newPlaybookButton: ActionButton = {
    label: 'New Playbook',
    path: isPipelinePlaybooks ? '/labs/playbooks/new' : '/labs/integrations/new',
    iconName: 'PlusCircle',
    variant: 'primary',
    // state will be passed via Link component's state prop implicitly
  };

  const actions: ActionButton[] = [newPlaybookButton];

  const loadPlaybooks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchAllPlaybooks();
      // Filter playbooks based on the current type
      setPlaybooks(data.filter(pb => pb.type === playbookType));
    } catch (err) {
      console.error('Failed to fetch playbooks:', err);
      setError('Failed to load playbooks. Please try again.');
      showNotification('error', 'Failed to load playbooks.');
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, playbookType]); // Add playbookType to dependencies

  useEffect(() => {
    loadPlaybooks();
  }, [loadPlaybooks]);

  const getPlaybookIcon = (type: string) => {
    switch (type) {
      case 'pipeline':
        return <Pipelines className="text-gray-500" />; // Use BookOpen for pipeline
      case 'integration':
        return <Destinations className="text-gray-500" />; // Use Destinations for integration, removed size prop
      default:
        return <Pipelines className="text-gray-500" />;
    }
  };

  const handleRowClick = (playbook: Playbook) => {
    // Use the correct path based on the playbook type
    const basePath = playbook.type === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';
    navigate(`${basePath}/${playbook.id}/edit/details`, { state: { playbookName: playbook.name, playbookType: playbook.type } });
  };

  const handleDeleteClick = (playbook: Playbook) => {
    setPlaybookToDelete(playbook);
    setShowConfirmDialog(true);
  };

  const handleDuplicateClick = useCallback(async (playbook: Playbook) => {
    try {
      const newName = `${playbook.name} (Copy)`;
      await duplicatePlaybook(playbook.id, newName);
      showNotification('success', `Playbook "${newName}" created successfully`);
      loadPlaybooks(); // Reload playbooks after duplication
    } catch (err) {
      console.error('Failed to duplicate playbook:', err);
      showNotification('error', `Failed to duplicate playbook "${playbook.name}".`);
    }
  }, [loadPlaybooks, showNotification]);

  const confirmDelete = useCallback(async () => {
    if (playbookToDelete) {
      try {
        await deletePlaybook(playbookToDelete.id);
        showNotification('success', `Playbook "${playbookToDelete.name}" deleted successfully`);
        loadPlaybooks(); // Reload playbooks after deletion
      } catch (err) {
        console.error('Failed to delete playbook:', err);
        showNotification('error', `Failed to delete playbook "${playbookToDelete.name}".`);
      } finally {
        setShowConfirmDialog(false);
        setPlaybookToDelete(null);
      }
    }
  }, [playbookToDelete, loadPlaybooks, showNotification]);

  const cancelDelete = useCallback(() => {
    setShowConfirmDialog(false);
    setPlaybookToDelete(null);
  }, []);

  const playbookColumns: ColumnDef<Playbook>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const basePath = row.original.type === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';
        const toPath = `${basePath}/${row.original.id}/edit/details`;
        return (
          <Link
            to={toPath}
            state={{ playbookName: row.original.name, playbookType: row.original.type }}
            className="flex items-center group" // Add group for hover effects
            onClick={(e) => e.stopPropagation()} // Prevent TableComponent's onRowClick
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full mr-1">
              {getPlaybookIcon(row.original.type)}
            </div>
            <span className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors duration-200">
              {row.original.name}
            </span>
          </Link>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => row.original.description || 'N/A',
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${
          row.original.type === 'pipeline' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
          row.original.type === 'integration' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {row.original.type}
        </span>
      ),
    },
    {
      accessorKey: 'version',
      header: 'Version',
      cell: ({ row }) => row.original.version || 'N/A',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            className="text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateClick(row.original);
            }}
          >
            <Copy className="h-5 w-5" />
          </button>
          <button
            className="text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.original);
            }}
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-300 transition-colors duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row.original);
            }}
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ], [handleRowClick, handleDeleteClick, handleDuplicateClick]);

  return (
    <div className="flex flex-col h-full">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-10 max-w-9xl mx-auto">
          <div className="space-y-8">
            <TableComponent
              title="Playbooks"
              data={playbooks}
              columns={playbookColumns}
              onRowClick={handleRowClick}
              isLoading={isLoading}
              isError={!!error}
              errorContent={
                <div className="flex items-center justify-center py-4 text-red-500">
                  <AlertCircle className="h-6 w-6 mr-3" />
                  <span>{error}</span>
                </div>
              }
              buttons={<></>} // Moved "Add Playbook" to LabsAppBar
            />
          </div>
        </div>
      </div>
      {showConfirmDialog && playbookToDelete && (
        <ConfirmDialog
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Confirm Playbook Deletion"
          message={`Are you sure you want to delete the playbook "${playbookToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          isDanger={true}
        />
      )}
    </div>
  );
};

export default PlaybookListPage;
